import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {DraggableCore, DraggableData} from 'react-draggable';

const styles = require('./Resizer.less');
const cx = classnames.bind(styles);

export enum ResizePhase
{
    Start = 'startPhase',
    Progress = 'progressPhase',
    Stop = 'stopPhase'
}

export interface ResizeEvent
{
    deltaX:number;
    deltaY:number;
    x:number;
    y:number;
    lastX:number;
    lastY:number;
    phase:ResizePhase;
}

export type OnResizeEventFn = (event:ResizeEvent) => (void);
export type OnDblClickEventFn = (event:React.MouseEvent<HTMLDivElement>) => (void);

export enum DragGhostDirection
{
    Left = 'left',
    Top = 'top',
    Right = 'right',
    Bottom = 'bottom'
}

export interface ResizerProps
{
    key?:string;
    isVisible:boolean,
    className?:string;
    style?:any;
    isVertical:boolean;
    dragGhostDirection?:DragGhostDirection;
    dragGhostOffset?:number;
    onResize:OnResizeEventFn;
    onDblClick?:OnDblClickEventFn;
}

export interface IResizerState
{
    isDragInProgress:boolean;
    dragGhostOffset:number;
}

@observer
export class Resizer extends React.Component<ResizerProps, any>
{
    @observable
    private _state:IResizerState;

    public constructor(props:ResizerProps)
    {
        super(props);

        this._initState();
    }

    @action
    componentWillReceiveProps({dragGhostOffset}:ResizerProps)
    {
        if( dragGhostOffset !== undefined )
        {
            this._state.dragGhostOffset = dragGhostOffset;
        }
    }

    private _initState()
    {
        const {dragGhostOffset} = this.props;

        this._state = {
            isDragInProgress: false,
            dragGhostOffset: dragGhostOffset !== undefined ? dragGhostOffset : 0
        };
    }

    @action
    private _onDragStart = (reactEvent:any, data:DraggableData):void =>
    {
        const {dragGhostOffset} = this.props;

        this._state.isDragInProgress = true;

        // uncontrolled usage
        if( dragGhostOffset === undefined )
        {
            this._state.dragGhostOffset = 0;
        }

        this._dispatchResize({...data, phase: ResizePhase.Start});
    };

    @action
    private _onDragInProgress = (reactEvent:any, data:DraggableData):void =>
    {
        const {dragGhostOffset} = this.props;

        this._dispatchResize({...data, phase: ResizePhase.Progress});

        // uncontrolled usage
        if( dragGhostOffset === undefined )
        {
            this._state.dragGhostOffset += data.deltaX;
        }
    };

    @action
    private _onDragStop = (reactEvent:any, data:DraggableData):void =>
    {
        this._state.isDragInProgress = false;

        this._dispatchResize({...data, phase: ResizePhase.Stop});
    };

    private _dispatchResize(data:any)
    {
        const {onResize} = this.props;

        if( onResize )
        {
            onResize(data);
        }
    }

    private _onDblClick = (event:React.MouseEvent<HTMLDivElement>):void =>
    {
        const {onDblClick} = this.props;

        if( onDblClick )
        {
            onDblClick(event);
        }
    };

    render()
    {
        const {
            isVertical,
            dragGhostDirection,
            className,
            style,
            isVisible
        } = this.props;

        const {
            isDragInProgress,
            dragGhostOffset
        } = this._state;

        const showGhostOverlay = isDragInProgress && dragGhostDirection;

        return (
            <DraggableCore
                onStart={this._onDragStart}
                onDrag={this._onDragInProgress}
                onStop={this._onDragStop}
                enableUserSelectHack={true}
            >

                <div
                    data-type={Resizer.ElementDataType}
                    className={cx(
                        'baseResizer',
                        {
                            hiddenResizer: !isVisible,
                            verticalResizer: isVertical,
                            horizontalResizer: !isVertical,
                            dragInProgress: isDragInProgress
                        },
                        className
                    )}
                    style={style}
                    onDoubleClick={this._onDblClick}
                >
                    {!showGhostOverlay && <div className={cx('visibleStrip')} />}
                    {
                        showGhostOverlay &&
                        <div
                            className={cx('ghostOverlay', {[dragGhostDirection]: true})}
                            style={{
                                [dragGhostDirection]: `${dragGhostOffset}px`
                            }}
                        >
                            <div className={cx('visibleStrip')} />
                        </div>
                    }
                </div>
            </DraggableCore>
        );
    }

    public static ElementDataType = 'resizer';
}
